import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _createClass from "@babel/runtime/helpers/createClass";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import { CanvasRender } from './CanvasRender';
import { Scene, CacheGroup } from './Scene';
import { useAnimation, shouldResetObjects, getPointAtArc, renderArcWithWidth } from './utils';
var ObjectName;
(function (ObjectName) {
  ObjectName["arcContainer"] = "arcContainer";
  ObjectName["arcInnerBlank"] = "arcInnerBlank";
  ObjectName["arcInner"] = "arcInner";
  ObjectName["arcInnerDashline"] = "arcInnerDashline";
  ObjectName["meterText"] = "meterText";
  ObjectName["meterPoint"] = "meterPoint";
})(ObjectName || (ObjectName = {}));
var getRelativeValue = function getRelativeValue(relative) {
  return function (val) {
    return relative * val / 158;
  };
};
export var GaugeRender = /*#__PURE__*/_createClass(function GaugeRender(ratio) {
  var _this = this;
  _classCallCheck(this, GaugeRender);
  this.showValue = 0;
  this.basicRadius = 0;
  this.dispose = function () {
    _this.stopPlaying();
    _this.canvasRender.dispose();
    _this.scene.clean();
    _this.showValueChangeListener = function () {};
  };
  this.getShowValue = function () {
    return _this.showValue;
  };
  this.addShowValueUpdateListener = function (listener) {
    _this.showValueChangeListener = listener;
  };
  this.removeShowValueUpdateListener = function () {
    _this.showValueChangeListener = undefined;
  };
  this.onElementRef = function (canvasElem) {
    if (!canvasElem) {
      return;
    }
    _this.canvasRender.onElementRef(canvasElem);
    var _this$canvasRender$ge = _this.canvasRender.getSize(),
      width = _this$canvasRender$ge.width,
      height = _this$canvasRender$ge.height;
    _this.calculater = getRelativeValue(Math.min(width, height));
    _this.basicRadius = Math.min(width, height) / 2 * 0.9;
    _this.initRenderObjects();
    _this.canvasRender.render();
  };
  this.updateOptions = function (options) {
    var prevValue = _this.gaugeOptions.value;
    var resetObjects = shouldResetObjects(options, _this.gaugeOptions);
    Object.assign(_this.gaugeOptions, options);
    if (resetObjects) {
      _this.initRenderObjects();
      _this.canvasRender.render();
    }
    if (_this.gaugeOptions.animation === 0) {
      _this.showValue = _this.gaugeOptions.value;
      _this.canvasRender.render();
      return;
    }
    if (prevValue !== _this.gaugeOptions.value) {
      _this.stopPlaying();
      _this.startPlaying();
    }
  };
  this.startPlaying = function () {
    var startValue = _this.showValue;
    var _this$gaugeOptions = _this.gaugeOptions,
      stopValue = _this$gaugeOptions.value,
      duration = _this$gaugeOptions.animation,
      timingFunction = _this$gaugeOptions.timingFunction;
    var dest = stopValue - startValue;
    _this.animation = useAnimation(duration, timingFunction, function (progress) {
      var _a;
      var nextValue = startValue + progress * dest;
      _this.showValue = nextValue;
      (_a = _this.showValueChangeListener) === null || _a === void 0 ? void 0 : _a.call(_this);
      _this.canvasRender.render();
      if (progress >= 1) {
        _this.stopPlaying();
      }
    });
    _this.animation.start();
  };
  this.stopPlaying = function () {
    if (_this.animation) {
      _this.animation.stop();
      _this.animation = undefined;
    }
  };
  this.createBackgroundCache = function () {
    var _a;
    var backgroundLayer = new CacheGroup('background');
    var _this$canvasRender$ge2 = _this.canvasRender.getSize(),
      width = _this$canvasRender$ge2.width,
      height = _this$canvasRender$ge2.height;
    var startAngle = 0.85 * Math.PI;
    var endAngle = 2.15 * Math.PI;
    var centerX = width / 2;
    var centerY = height / 2;
    var basicRadius = _this.basicRadius,
      calculater = _this.calculater;
    var lineDashRadius = basicRadius * 0.5;
    backgroundLayer.add({
      id: ObjectName.arcInnerDashline,
      type: 'arc',
      x: centerX,
      y: centerY,
      startAngle: startAngle,
      endAngle: endAngle,
      stroke: '#4D5869',
      radius: lineDashRadius,
      lineDashSegments: [calculater(1), calculater(2)]
    });
    backgroundLayer.add({
      id: ObjectName.arcContainer,
      x: 0,
      y: 0,
      type: 'custom',
      render: function render(ctx) {
        renderArcWithWidth(ctx, {
          arcWidth: calculater(15),
          startAngle: startAngle,
          endAngle: endAngle,
          centerX: centerX,
          centerY: centerY,
          radius: basicRadius * 0.7
        });
        ctx.globalAlpha = 0.23;
        ctx.strokeStyle = 'rgb(181, 198, 239)';
        ctx.stroke();
      }
    });
    backgroundLayer.add({
      id: ObjectName.arcInnerBlank,
      type: 'custom',
      render: function render(ctx) {
        renderArcWithWidth(ctx, {
          arcWidth: calculater(11),
          startAngle: startAngle,
          endAngle: endAngle,
          centerX: centerX,
          centerY: centerY,
          radius: basicRadius * 0.7
        });
        ctx.globalAlpha = 0.23;
        ctx.fillStyle = 'rgb(148, 167, 207)';
        ctx.fill();
      }
    });
    var _this$gaugeOptions2 = _this.gaugeOptions,
      maxValue = _this$gaugeOptions2.maxValue,
      minValue = _this$gaugeOptions2.minValue;
    var font = _this.gaugeOptions.font || "".concat(Math.floor(calculater(14)), "px bold sans-serif");
    var valueSize = maxValue - minValue;
    var meterTextRadius = basicRadius * 0.97;
    if ((_a = _this.gaugeOptions) === null || _a === void 0 ? void 0 : _a.meters) {
      _this.gaugeOptions.meters.forEach(function (value, index) {
        var percent = value / valueSize;
        var angle = startAngle + (endAngle - startAngle) * percent;
        var _getPointAtArc = getPointAtArc(centerX, centerY, meterTextRadius, angle),
          _getPointAtArc2 = _slicedToArray(_getPointAtArc, 2),
          x = _getPointAtArc2[0],
          y = _getPointAtArc2[1];
        backgroundLayer.add({
          id: "".concat(ObjectName.meterText, "-").concat(index),
          type: 'text',
          content: value.toString(),
          x: x,
          y: y,
          font: font
        });
        var _getPointAtArc3 = getPointAtArc(centerX, centerY, lineDashRadius, angle),
          _getPointAtArc4 = _slicedToArray(_getPointAtArc3, 2),
          rx = _getPointAtArc4[0],
          ry = _getPointAtArc4[1];
        backgroundLayer.add({
          id: "".concat(ObjectName.meterPoint, "-").concat(index),
          type: 'arc',
          radius: calculater(1),
          startAngle: 0,
          endAngle: 2 * Math.PI,
          fill: 'rgb(3, 3, 3)',
          x: rx,
          y: ry
        });
      });
    }
    return backgroundLayer;
  };
  this.initRenderObjects = function () {
    _this.scene.clean();
    var _this$canvasRender$ge3 = _this.canvasRender.getSize(),
      width = _this$canvasRender$ge3.width,
      height = _this$canvasRender$ge3.height;
    var startAngle = 0.85 * Math.PI;
    var endAngle = 2.15 * Math.PI;
    var centerX = width / 2;
    var centerY = height / 2;
    var basicRadius = _this.basicRadius,
      calculater = _this.calculater;
    var backgroundCache = _this.createBackgroundCache();
    _this.scene.add(backgroundCache);
    _this.scene.add({
      id: ObjectName.arcInner,
      type: 'custom',
      render: function render(ctx) {
        var _this$gaugeOptions3 = _this.gaugeOptions,
          minValue = _this$gaugeOptions3.minValue,
          maxValue = _this$gaugeOptions3.maxValue;
        var valuePercent = _this.showValue / (maxValue - minValue);
        valuePercent = Math.max(Math.min(valuePercent, 1), 0);
        var valueArc = startAngle + (endAngle - startAngle) * valuePercent;
        renderArcWithWidth(ctx, {
          arcWidth: calculater(11),
          startAngle: startAngle,
          endAngle: valueArc,
          centerX: centerX,
          centerY: centerY,
          radius: basicRadius * 0.7
        });
        var fillColor = _this.gaugeOptions.fillColor;
        if (fillColor) {
          if (typeof fillColor === 'string') {
            ctx.fillStyle = fillColor;
          } else {
            var len = fillColor.length;
            var lastIndex = len - 1;
            var gr = ctx.createLinearGradient(centerX - basicRadius, 0, centerX + basicRadius, 0);
            fillColor.forEach(function (color, index) {
              gr.addColorStop(index / lastIndex, color);
            });
            ctx.fillStyle = gr;
          }
          ctx.fill();
        }
        var _getPointAtArc5 = getPointAtArc(centerX, centerY, basicRadius * 0.73, valueArc),
          _getPointAtArc6 = _slicedToArray(_getPointAtArc5, 2),
          pointX = _getPointAtArc6[0],
          pointY = _getPointAtArc6[1];
        ctx.beginPath();
        ctx.arc(pointX, pointY, calculater(2), 0, Math.PI * 2);
        ctx.fillStyle = '#FFF';
        ctx.fill();
      }
    });
  };
  var scene = new Scene();
  this.scene = scene;
  this.canvasRender = new CanvasRender(this.scene, ratio);
  this.calculater = function (v) {
    return v;
  };
  this.gaugeOptions = {
    ratio: ratio,
    font: '',
    value: 0,
    minValue: 0,
    maxValue: 100,
    meters: [0, 20, 40, 60, 80, 100],
    fillColor: ['#FF0000', '#FFED4A'],
    animation: 0,
    timingFunction: function timingFunction(t) {
      return Math.pow(t, 0.5);
    }
  };
});